import { AppToastOutlet, useAppToastRef } from './app-toast-provider';
import { checkDependencyCycle } from '../general/dependency-cycles';
import { restoredModule } from '../general/module-history';
import { useUnsavedActions, useUnsavedDraft } from '../general/unsaved-changes-context';
import { estimatedMinutesError } from '../general/estimated-minutes';
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useBookmarkItems } from '../general/bookmark-selection';
import { UserDto, ModuleUserDto, ModuleDto, MessageDto, Pager, createPager, SearchQueryParameters, serializeSearchQueryParameters, GetModulesApiResponseDto } from "../general/dtos";
import { Api } from "../general/api";
import { Toast } from "primereact/toast";
import { createEmptyFilter, createEmptyOrderBy, DataTableFilter, DataTableOrderBy } from "../general/data-grid-utils";
import { useApi } from "./api-provider";

export interface ModuleValidationErrors {
    name?: string;
    content?: string;
    instructions?: string;
    description?: string;
    estimated_minutes?: string;
    prerequisites?: string;
}

export interface ModuleManagementDataContextValue {
    toastRef: React.RefObject<Toast | null>;
    
    // modules
    currentFolderId: number | null;
    setCurrentFolderId: (id: number | null) => void;
    modules: ModuleDto[];
    modulesLoading: boolean;
    modulesError: string | null;
    modulesPager: Pager;
    modulesFilters: DataTableFilter;
    modulesSorting: DataTableOrderBy;
    fetchModules: (newQuery: SearchQueryParameters) => Promise<void>;
    selectedModules: ModuleDto[];
    setSelectedModules: (modules: ModuleDto[]) => void;
    selectAllModules: () => void;
    selectNoModules: () => void;
    invertModuleSelections: () => void;

    // users
    allUsers: UserDto[];
    allUsersLoading: boolean;
    allUsersError: string | null;
    loadAllUsers: () => Promise<void>;

    allModules: ModuleDto[];
    allModulesLoading: boolean;
    allModulesError: string | null;
    loadAllModules: () => Promise<void>;

    // module users
    moduleUsers: Record<string, ModuleUserDto[]>;
    moduleUsersLoading: boolean;
    moduleUsersError: string | null;
    loadModuleUsers: (userId: string) => Promise<void>;
    setModuleUsersForModule: (moduleKey: string, users: ModuleUserDto[]) => void;

    // Selected module (full DTO with content)
    selectedModuleKey: string | null;
    selectedModuleFull: ModuleDto | null;
    selectedModuleLoading: boolean;
    selectedModuleError: string | null;

    // Actions
    mode: "view" | "edit" | "create";
    loadModule: (moduleKey: string) => Promise<ModuleDto | undefined>;
    selectModule: (moduleKey: string) => void;
    startEditModule: (moduleKey: string) => void;
    startCreateModule: () => void;
    initNewModule: () => void;

    // new update functions
    validationErrors: ModuleValidationErrors;
    updateModuleField: (field: keyof ModuleDto, value: any) => void;
    updateModulePrerequisites: (moduleKeys: string[]) => void;
    validateModule: () => string[] | null;
    restoreRevision: (snapshot: ModuleDto) => void;
    saveModule: () => Promise<boolean>;
    deleteModule: (module_key: string) => void;
    cancelEditing: () => void;
}

export const ModuleManagementDataContext = createContext<ModuleManagementDataContextValue | null>(null);

export function ModuleManagementDataProvider({ children }: { children: React.ReactNode }) {
  const toastRef = useAppToastRef();
  const moduleSaveInFlight = useRef(false);
  const { requestAction, withoutWarning, afterSave } = useUnsavedActions();
  const api: Api = useApi();
  const [validationErrors, setValidationErrors] = useState<ModuleValidationErrors>({});

  const [currentFolderId, setCurrentFolderId] = useState<number | null>(() => {
    const value = new URLSearchParams(window.location.search).get('folder');
    return value && /^[1-9][0-9]*$/.test(value) ? Number(value) : null;
  });
  const currentFolderRef = useRef<number | null>(null);
  currentFolderRef.current = currentFolderId;
  const modulesGenerationRef = useRef(0);

  // modules
  const [initialListQuery] = useState<SearchQueryParameters | undefined>(() => window.history.state?.usr?.moduleListQuery);
  const [modules, setModules] = useState<ModuleDto[]>([]);
  const [modulesPager, setModulesPager] = useState<Pager>(() => initialListQuery?.pager ?? createPager(1, 10, 0));
  const [modulesFilters, setModulesFilters] = useState<DataTableFilter>(() => initialListQuery?.filter ?? createEmptyFilter());
  const [modulesSorting, setModulesSorting] = useState<DataTableOrderBy>(() => initialListQuery?.orderBy ?? createEmptyOrderBy());
  const [modulesLoading, setModulesLoading] = useState<boolean>(false);
  const [modulesError, setModulesError] = useState<string | null>(null);
  const lastModulesQueryRef = useRef<string | null>(null);
  const loadBookmarkedModule = useCallback(async (key: string) => {
    const module = await api.getModule(key);
    if (!module) throw new Error('Module not found.');
    return module;
  }, [api]);
  const { items: selectedModules, setItems: setSelectedModules, keys: bookmarkedModuleKeys, setKeys: setModuleBookmarkKeys, error: bookmarkError } = useBookmarkItems<ModuleDto>('module', api ? loadBookmarkedModule : null, moduleBookmarkKey);
  const moduleLoadGeneration = useRef(0);
  
  // modules
  const [allModules, setAllModules] = useState<ModuleDto[]>([]);
  const [allModulesLoading, setAllModulesLoading] = useState<boolean>(false);
  const [allModulesError, setAllModulesError] = useState<string | null>(null);
  const modulesInFlight = useRef<Promise<void> | null>(null);

  // users
  const [allUsers, setAllUsers] = useState<UserDto[]>([]);
  const [allUsersLoading, setAllUsersLoading] = useState<boolean>(false);
  const [allUsersError, setAllUsersError] = useState<string | null>(null);
  const usersInFlight = useRef<Promise<void> | null>(null);

  // module users
  const [moduleUsers, setModuleUsers] = useState<Record<string, ModuleUserDto[]>>({});
  const [moduleUsersLoading, setModuleUsersLoading] = useState<boolean>(false);
  const [moduleUsersError, setModuleUsersError] = useState<string | null>(null);
  const moduleUsersInFlight = useRef<Record<string, Promise<void> | null>>({});

  // selected module
  const [selectedModuleKey, setSelectedModuleKey] = useState<string | null>(null);
  const [selectedModuleFull, setSelectedModuleFull] = useState<ModuleDto | null>(null);
  const selectedModuleRef = useRef(selectedModuleFull);
  selectedModuleRef.current = selectedModuleFull;
  const [originalModuleFull, setOriginalModuleFull] = useState<ModuleDto | null>(null);
  const [selectedModuleLoading, setSelectedModuleLoading] = useState(false);
  const [selectedModuleError, setSelectedModuleError] = useState<string | null>(null);

  // mode
  const [mode, setMode] = useState<"view" | "edit" | "create">("view");

  const selectAllModules = useCallback(() => {
    setSelectedModules(modules);
  }, [modules, setSelectedModules]);

  const selectNoModules = useCallback(() => {
    setSelectedModules([]);
  }, [setSelectedModules]);

  const invertModuleSelections = useCallback(() => {
    setSelectedModules(prev => {
        const selectedKeys = new Set(prev.map(m => m.module_key));
        return modules.filter(m => !selectedKeys.has(m.module_key));
    });
  }, [modules, setSelectedModules]);

  const fetchModules = useCallback(async (newQuery: SearchQueryParameters) => {
    newQuery = { ...newQuery, folderId: currentFolderRef.current };
    const key = serializeSearchQueryParameters(newQuery);
    if (key === lastModulesQueryRef.current) {
        return;
    }

    const generation = ++modulesGenerationRef.current;
    lastModulesQueryRef.current = key;
    setModulesError(null);
    setModulesLoading(true);

    try {
        const response: GetModulesApiResponseDto = await api.getModules(newQuery.pager.page, newQuery.pager.pageSize,
            newQuery.orderBy.orderBy, newQuery.orderBy.orderDirection,
            newQuery.filter.filterBy, newQuery.filter.filterValue, newQuery.folderId);

        if (generation !== modulesGenerationRef.current) return;
        setModules(response.data);
        setModulesPager({
            page: response.pager.page,
            pageSize: response.pager.pageSize,
            totalRows: response.pager.totalRows,
            totalPages: response.pager.totalPages
        });
        setModulesSorting({ orderBy: newQuery.orderBy.orderBy, orderDirection: newQuery.orderBy.orderDirection }); 
        setModulesFilters({ filterBy: newQuery.filter.filterBy, filterValue: newQuery.filter.filterValue });
    } catch (error: any) {
        if (generation !== modulesGenerationRef.current) return;
        const message: string = error.response?.data?.message ?? 'Failed to load modules';
        setModulesError(message);
        setSelectedModuleFull(null);
    } finally {
        if (generation === modulesGenerationRef.current) {
            setModulesLoading(false);
            lastModulesQueryRef.current = null;
        }
    }
  }, []);

  const loadAllModules = async (): Promise<void> => {
    if (modulesInFlight.current) {
        return modulesInFlight.current;
    }

    setAllModulesLoading(true);
    setAllModulesError(null);

    const promise = api.getModules(0, 10000, '', '', '', '')
    .then((data) => {
      setAllModules(data.data);
      setAllModulesLoading(false);
      modulesInFlight.current = null;
    })
    .catch((error) => {
      const message: string = error.response?.data?.message ?? 'Load error';
      setAllModulesError(message);
      setAllModulesLoading(false);
      modulesInFlight.current = null;
    });

    modulesInFlight.current = promise;
    return promise;
  };

  const loadAllUsers = async (): Promise<void> => {
    if (usersInFlight.current) {
        return usersInFlight.current;
    }

    setAllUsersLoading(true);
    setAllUsersError(null);

    const promise = api.getUsers(0, 10000, '', '', '', '')
    .then((data) => {
      setAllUsers(data.data);
      setAllUsersLoading(false);
      usersInFlight.current = null;
    })
    .catch((error) => {
      const message: string = error.response?.data?.message ?? 'Load error';
      setAllUsersError(message);
      setAllUsersLoading(false);
      usersInFlight.current = null;
    });

    usersInFlight.current = promise;
    return promise;
  };

  const loadModuleUsers = async (moduleKey: string): Promise<void> => {
    if (moduleUsersInFlight.current[moduleKey]) {
        return moduleUsersInFlight.current[moduleKey]!;
    }

    setModuleUsersLoading(true);
    setModuleUsersError(null);

    const promise = api.getModuleUsers(moduleKey)
      .then((data) => {
        setModuleUsers(prev => ({ ...prev, [moduleKey]: data.data }));
        setModuleUsersLoading(false);
        moduleUsersInFlight.current[moduleKey] = null;
      })
      .catch((error) => {
        const message: string = error.response?.data?.message ?? 'Load error';
        setModuleUsersError(message);
        setModuleUsersLoading(false);
        moduleUsersInFlight.current[moduleKey] = null;
      });

    moduleUsersInFlight.current[moduleKey] = promise;
    return promise;
  };

  useEffect(() => {
    loadAllModules();
    loadAllUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setModuleUsersForModule = (moduleKey: string, users: ModuleUserDto[]): void => {
      setModuleUsers(prev => ({
        ...prev,
        [moduleKey]: users
      }));
  };

  // Load full module DTO by key
  const loadModule = useCallback(async (moduleKey: string): Promise<ModuleDto | undefined> => {
    const generation = ++moduleLoadGeneration.current;
    setSelectedModuleLoading(true);
    setSelectedModuleError(null);

    try {
        const dto: ModuleDto | null = await api.getModule(moduleKey);
        if (generation !== moduleLoadGeneration.current) return;
        if (!dto) throw new Error('Module not found.');
        const resolvedDto: ModuleDto = {...dto } as ModuleDto;
        guard.markSaved(resolvedDto);
        setSelectedModuleFull(resolvedDto);
        setOriginalModuleFull(resolvedDto);
        return resolvedDto;
    } catch (error: any) {
        if (generation !== moduleLoadGeneration.current) return;
        const message: string = error.response?.data?.message ?? 'Failed to load module';
        setSelectedModuleError(message);
        setSelectedModuleFull(null);
        return undefined;
    } finally {
        if (generation === moduleLoadGeneration.current) setSelectedModuleLoading(false);
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Called when user selects a module in the left panel
  const selectModule = useCallback((moduleKey: string) => {
    setValidationErrors({});
    setMode("view");
    setSelectedModuleKey(moduleKey);
    setModuleBookmarkKeys([moduleKey]);
    loadModule(moduleKey);
  }, [loadModule, setModuleBookmarkKeys]);

  const startEditModule = useCallback((moduleKey: string) => {
    setMode("edit");
    setSelectedModuleKey(moduleKey);
    setModuleBookmarkKeys([moduleKey]);
    loadModule(moduleKey);
  }, [loadModule, setModuleBookmarkKeys]);

  const selectedModuleKeys = JSON.stringify(bookmarkedModuleKeys);
  useEffect(() => {
    if (bookmarkedModuleKeys.length === 1) {
      const key = bookmarkedModuleKeys[0];
      if (key !== selectedModuleKey) {
        setMode('view');
        setSelectedModuleKey(key);
        setSelectedModuleFull(null);
        void loadModule(key);
      }
    } else if (mode !== 'create') {
      moduleLoadGeneration.current++;
      setSelectedModuleKey(null);
      setSelectedModuleFull(null);
      setSelectedModuleLoading(false);
      setSelectedModuleError(null);
      setMode('view');
    }
    // Selection changes restore saved content; editing the same selection does not reload it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModuleKeys, loadModule]);

  // Initialize a new empty module (create mode)
  const initNewModule = useCallback(() => {
    setSelectedModuleKey(null);
    setModuleBookmarkKeys([]);

    const empty: ModuleDto = {
        folder_id: currentFolderRef.current,
        module_key: "",
        name: "New Module",
        description: "",
        content: "",
        instructions: "",
        created_at: "",
        updated_at: "",
        fragments: undefined,
    };

    setSelectedModuleFull(empty);
    setSelectedModuleLoading(false);
    setSelectedModuleError(null);
  }, [setModuleBookmarkKeys]);

  const startCreateModule = useCallback(() => {
    setOriginalModuleFull(selectedModuleRef.current);
    setMode("create");
    initNewModule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initNewModule, selectedModuleFull]);

  const updateModuleField = useCallback((field: keyof ModuleDto, value: any) => {
      setSelectedModuleFull(prev => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const updateModulePrerequisites = useCallback((moduleKeys: string[]) => {
    setSelectedModuleFull(prev => {
        if (!prev) return prev;

        return {
            ...prev,
            prerequisites: moduleKeys.map(key => ({
                module_key: key,
                is_optional: false   // default; can be edited later
            }))
        };
    });
  }, []);

  const validateModule = useCallback((): string[] | null => {
    setValidationErrors({});
    if (!selectedModuleFull) return ["No module loaded"];

    const errors: string[] = [];
    const namedErrors: ModuleValidationErrors = {};
    const c = selectedModuleFull;

    const durationError = estimatedMinutesError(c.estimated_minutes);
    if (durationError) { errors.push(durationError); namedErrors.estimated_minutes = durationError; }

    // Name required
    if (!c.name || c.name.trim().length === 0) {
        errors.push("Module name is required.");
        namedErrors.name = "Module name is required.";
    }

    // Content required 
    if (!c.content || c.content.trim().length === 0) {
        errors.push("Module content is required.");
        namedErrors.content = "Module content is required.";
    }

    // Instructions required
    if (!c.instructions || c.instructions.trim().length === 0) {
        errors.push("Module instructions are required.");
        namedErrors.instructions = "Module instructions are required.";
    }

    // Description optional but recommended
    if (!c.description || c.description.trim().length === 0) {
        errors.push("Module description is required.");
        namedErrors.description = "Module description is required.";
    }

    // Module prerequisites must not include itself
    const prereqKeys = c.prerequisites?.map(p => p.module_key);
    if (prereqKeys?.includes(c.module_key)) {
        errors.push("A module cannot depend on itself.");
        namedErrors.prerequisites = "A module cannot depend on itself.";
    }
   
    setValidationErrors(namedErrors);
    return errors.length > 0 ? errors : null;
  }, [selectedModuleFull]);
  
    const createModule = useCallback(async () => {
      if (!selectedModuleFull) {
          return false;
      }
  
      const payload = {
          folder_id: selectedModuleFull.folder_id,
          name: selectedModuleFull.name,
          description: selectedModuleFull.description,
          estimated_minutes: selectedModuleFull.estimated_minutes ?? null,
          content: selectedModuleFull.content,
          instructions: selectedModuleFull.instructions,
          prerequisites: selectedModuleFull.prerequisites
      };
  
      try {
        const created: ModuleDto | MessageDto | null = await api.createModule(payload as ModuleDto);
        if (created && !created.hasOwnProperty('message')) {
          setModules(prev => (created as ModuleDto).folder_id === currentFolderRef.current ? [...prev, created as ModuleDto] : prev);
          setAllModules(prev => [...prev, created as ModuleDto]); // add to the full list of modules
          guard.markSaved(created);
          setSelectedModuleFull(created as ModuleDto);
          afterSave(() => setSelectedModules([created as ModuleDto]));
          setSelectedModuleKey((created as ModuleDto).module_key);
          setMode("view");
          return true;
        } else {
          const message: string = (created && created.hasOwnProperty('message'))
              ? (created as MessageDto).message
              : 'The newly created module was not retrieved from the server propertly. Refresh the page to see if it was created.';
          setSelectedModuleError(message);
        }
      } catch (error: any) {
          const message: string = error.response?.data?.message ?? 'Failed to create module';
          setSelectedModuleError(message);
      } finally {
          setSelectedModuleLoading(false);
      }
      return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedModuleFull]);
  
    const updateModule = useCallback(async () => {
      if (!selectedModuleFull) return false;
  
      const payload = {
          module_key: selectedModuleFull.module_key,
          folder_id: selectedModuleFull.folder_id,
          name: selectedModuleFull.name,
          description: selectedModuleFull.description,
          estimated_minutes: selectedModuleFull.estimated_minutes ?? null,
          content: selectedModuleFull.content,
          instructions: selectedModuleFull.instructions,
          prerequisites: selectedModuleFull.prerequisites
      } as ModuleDto;
  
      try {
        const updated: ModuleDto | MessageDto | null = await api.updateModule(payload);
        if (updated && !updated.hasOwnProperty('message')) {
          setModules(prev =>
            prev.map(c => c.module_key === (updated  as ModuleDto).module_key ? updated as ModuleDto : c)
          );
          setAllModules(prev =>
            prev.map(c => c.module_key === (updated  as ModuleDto).module_key ? updated as ModuleDto : c)
          );
          guard.markSaved(updated);
          setSelectedModuleFull(updated as ModuleDto);
          setOriginalModuleFull(structuredClone(updated as ModuleDto));
          setMode("view");
          return true;
        } else {
          const message: string = (updated && updated.hasOwnProperty('message'))
              ? (updated as MessageDto).message
              : 'The updated module was not retrieved from the server propertly. Refresh the page to see if it was updated properly.';
          setSelectedModuleError(message);
        }
      } catch (error: any) {
          const message: string = error.response?.data?.message ?? 'Failed to save module';
          setSelectedModuleError(message);
      } finally {
          setSelectedModuleLoading(false);
      }
      return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedModuleFull]);
  
    const saveModule = useCallback(async () => {
      if (!selectedModuleFull || moduleSaveInFlight.current || mode === 'view') return false;
  
      const errors: string[] | null = validateModule();
      toastRef?.current?.clear();
      if (errors) {
          toastRef?.current?.show({
              severity: "error",
              summary: "Validation failed",
              detail: (
                  <ol style={{ marginLeft: "1rem", paddingLeft: "1rem", marginTop: "0.25rem" }}>
                      {errors.map((e, i) => (
                          <li key={i}>{e}</li>
                      ))}
                  </ol>
              ),
              life: 20000
          });
          return false;
      }
  
      moduleSaveInFlight.current = true;
      setSelectedModuleLoading(true);
      setSelectedModuleError(null);
      try {
        const cycle = await checkDependencyCycle({ key: selectedModuleFull.module_key, name: selectedModuleFull.name,
            prerequisites: (selectedModuleFull.prerequisites ?? []).map(item => item.module_key) }, async key => {
                const module = await api.getModule(key);
                return module ? { key: module.module_key, name: module.name,
                    prerequisites: (module.prerequisites ?? []).map(item => item.module_key) } : null;
            });
        if (cycle) {
            setValidationErrors(previous => ({ ...previous, prerequisites: cycle }));
            setSelectedModuleError(cycle);
            return false;
        }
        if (mode === "create") {
            if (!await createModule()) return false;
        } else if (mode === "edit") {
            if (!await updateModule()) return false;
        } else {
            return false; // view mode: do nothing
        }
  
        toastRef?.current?.clear();
        toastRef?.current?.show({
            severity: "success",
            summary: "Module saved",
            life: 4000
        });
        return true;
      } catch (error: any) {
          setSelectedModuleError(error.message ?? 'Could not verify prerequisites. Try saving again.');
          return false;
      } finally {
          moduleSaveInFlight.current = false;
          setSelectedModuleLoading(false);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, selectedModuleFull, createModule, updateModule, api, validateModule]);
  
    const cancelEditing = useCallback(() => {
      if (mode === "create") {
          // Cancel creation → go back to view mode with no module selected
          if (originalModuleFull) {
              setSelectedModuleFull(structuredClone(originalModuleFull));
          } else {
              setSelectedModuleFull(null);
          }
          setOriginalModuleFull(null);
          setSelectedModules(originalModuleFull ? [originalModuleFull] : []);
          setSelectedModuleKey(originalModuleFull?.module_key ?? null);
          setMode("view");
          return;
      }
  
      if (mode === "edit") {
          // Revert to last saved version
          if (originalModuleFull) {
              setSelectedModuleFull(structuredClone(originalModuleFull));
          }
          setMode("view");
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, originalModuleFull, setSelectedModules]);
  
    const deleteModule = useCallback(async (module_key: string) => {
      try {
        const deleteMessage: MessageDto = await api.deleteModule(module_key);
        if (deleteMessage && deleteMessage.message === 'OK') {
          setSelectedModules(current => current.filter(module => module.module_key !== module_key));
          setSelectedModuleFull(null);
          setOriginalModuleFull(null);
          setModules(prev => prev.filter(c => c.module_key !== module_key));
          setAllModules(prev => prev.filter(c => c.module_key !== module_key));
          setMode("view");
          toastRef.current?.clear();
          toastRef.current?.show({
              severity: "success",
              summary: "Module deleted",
              detail: `The module has been removed.`,
              life: 4000
          });
        } else {
          const message: string = (deleteMessage && deleteMessage.message)
              ? deleteMessage.message
              : 'The module might not have been deleted from the server propertly. Refresh the page to see if it was actually deleted.';
          setSelectedModuleError(message);
        }
      } catch (error: any) {
          const message: string = error.response?.data?.message ?? 'Failed to save module';
          setSelectedModuleError(message);
          setSelectedModuleFull(null);
      } finally {
          setSelectedModuleLoading(false);
      }
    }, [api, setSelectedModules, toastRef]);

  const guard = useUnsavedDraft({ active: mode !== 'view' && selectedModuleFull !== null,
    value: selectedModuleFull, baselineValue: originalModuleFull ?? selectedModuleFull, parameter: 'module', save: saveModule, discard: cancelEditing });

  const restoreRevision = (snapshot: ModuleDto) => requestAction(() => {
    const current = selectedModuleRef.current;
    if (!current || current.module_key !== snapshot.module_key) return;
    const restored = restoredModule(snapshot, current);
    if (mode === 'view') setOriginalModuleFull(structuredClone(current));
    setSelectedModuleFull(restored);
    setValidationErrors({}); setSelectedModuleError(null);
    setMode('edit');
  });

  return (
    <ModuleManagementDataContext.Provider
      value={{
        currentFolderId, setCurrentFolderId,
        toastRef,

        // modules
        modules,
        modulesLoading,
        modulesError,
        modulesPager,
        modulesFilters,
        modulesSorting,
        fetchModules,
        selectedModules,
        setSelectedModules,
        selectAllModules,
        selectNoModules,
        invertModuleSelections,

        allModules,
        allModulesLoading,
        allModulesError,
        loadAllModules,

        allUsers,
        allUsersLoading,
        allUsersError,
        loadAllUsers,

        moduleUsers,
        moduleUsersLoading,
        moduleUsersError,
        loadModuleUsers,
        setModuleUsersForModule,

        selectedModuleKey,
        selectedModuleFull,
        selectedModuleLoading,
        selectedModuleError: selectedModuleError || bookmarkError,

        // Actions
        mode,
        loadModule,
        selectModule: (moduleKey) => requestAction(() => selectModule(moduleKey)),
        startEditModule: (moduleKey) => requestAction(() => startEditModule(moduleKey)),
        startCreateModule: () => requestAction(() => startCreateModule()),
        initNewModule,

        // new update functions
        validationErrors,
        updateModuleField,
        updateModulePrerequisites,
        validateModule,
        saveModule, restoreRevision,
        deleteModule,
        cancelEditing: () => withoutWarning(cancelEditing),
      }}
    >
      {children}
      <AppToastOutlet toastRef={toastRef} />
    </ModuleManagementDataContext.Provider>
  );
}

const moduleBookmarkKey = (module: ModuleDto) => module.module_key;
